import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { supabaseConfig } from '@/shared/config/supabase'
import { createSafeAuthStorage } from '@/shared/supabase/authStorage'
import type { Database } from '@/shared/supabase/database.types'

/**
 * No-op lock: executa a operação sem adquirir lock do Navigator LockManager.
 * Evita timeout "Acquiring an exclusive Navigator LockManager lock... timed out"
 * que ocorre após logout, delete ou operações concorrentes. O lock padrão pode
 * ficar preso em PWAs ou quando o tab fica em background.
 * Trade-off: menor proteção contra races em múltiplas abas; preferível a bloquear o app.
 */
const lockNoOp = <R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> => fn()

const SUPABASE_CLIENT_OPTIONS = {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: lockNoOp,
    storage: typeof window !== 'undefined' ? createSafeAuthStorage() : undefined,
    experimental: { passkey: true },
  },
  /** Menos ruído no console; WebSocket só conecta quando há canal Realtime. */
  realtime: {
    logLevel: 'error' as const,
  },
}

let client: SupabaseClient<Database> | null = null

export const getSupabaseClient = (): SupabaseClient<Database> => {
  if (!client) {
    client = createClient<Database>(
      supabaseConfig.url,
      supabaseConfig.anonKey,
      SUPABASE_CLIENT_OPTIONS,
    )
  }

  return client
}

export const supabase = getSupabaseClient()